import React from "react";
import {
  PlxsTextField,
  PlxsText,
  PlxsTextLink,
  PlxsButton,
  PlxsField,
  PlxsAlert,
  PlxsIcon,
} from "@plexlogic/plexus-ui";
import { DrawData, Prize } from "../../types/DrawData";
import { WizardStepHeader } from "../index";
import WizardFooterNav from "../WizardFooterNav";
import WizardRadioButtonGroup from "../WizardRadioButtonGroup";
import WizardStepQuickCheck from "./WizardStepQuickCheck";
import {
  getHelpUrl,
  getTextSnippet,
} from "../../utils/wizardContentRegistry";
import { ScrutineerRequirementText } from "../drawDetails";

import { LARGEST_PRIZE_VALUE } from "../../constants/regulation";
import { scrollToFirstError, ErrorField } from "../../utils/scrollToFirstError";

interface PrizeConfigurationStepProps {
  drawData: DrawData;
  updateDrawData: (updates: Partial<DrawData>) => void;
  onNext: () => void;
  onBack: () => void;
  isLocked?: boolean;
}

// Scrutineer is required when total prize pool value exceeds this threshold (in AUD)
const SCRUTINEER_PRIZE_THRESHOLD = 10000;

// Prize value limits set as per regulatory requirements
// const LARGEST_PRIZE_VALUE = 200000; - now from regulations.ts
const SMALLEST_PRIZE_VALUE = 0;

// Helper to check if winners field has an error
const getWinnersError = (prize: Prize): string | undefined => {
  if (!prize.winners || prize.winners < 1) {
    return "Please enter a number greater than 0";
  }
  return undefined;
};

const getDrawValueError = (prize: Prize): string | undefined => {
  if (
    prize.estimatedValue === null ||
    prize.estimatedValue === undefined ||
    prize.estimatedValue < SMALLEST_PRIZE_VALUE
  ) {
    return "Please enter a value of 0 or more";
  }

  if (prize.estimatedValue > LARGEST_PRIZE_VALUE) {
    return `Single prizes are capped at $${LARGEST_PRIZE_VALUE.toLocaleString(
      "en-AU"
    )}. Drop this value to continue.`;
  }
  return undefined;
};

// Helper to check if reserves field has an error
const getReservesError = (prize: Prize): string | undefined => {
  if (prize.reserves < 0) {
    return "Reserves cannot be negative";
  }
  return undefined;
};

const PrizeConfigurationStep: React.FC<PrizeConfigurationStepProps> = ({
  drawData,
  updateDrawData,
  onNext,
  onBack,
  isLocked = false,
}) => {
  const [showErrors, setShowErrors] = React.useState(false);

  const addPrize = () => {
    const newPrizes = [
      ...drawData.prizes,
      { description: "", winners: 1, reserves: 2, estimatedValue: 0 },
    ];
    updateDrawData({ prizes: newPrizes });
  };

  const removePrize = (index: number) => {
    if (drawData.prizes.length > 1) {
      const newPrizes = drawData.prizes.filter((_, i) => i !== index);
      updateDrawData({ prizes: newPrizes });
    }
  };

  const updatePrize = (
    index: number,
    field: "description" | "winners" | "reserves" | "estimatedValue",
    value: string | number
  ) => {
    const newPrizes = [...drawData.prizes];
    newPrizes[index] = { ...newPrizes[index], [field]: value };

    const totalPrizePoolValue = newPrizes.reduce(
      (sum, prize) => sum + (prize.estimatedValue || 0) * (prize.winners || 0),
      0
    );

    // Set default scrutineer requirement to true when prize pool exceeds $10,000
    const updates: Partial<DrawData> = {
      prizes: newPrizes,
      totalPrizePoolValue,
    };

    // If prize pool drops below threshold, clear scrutineer requirement and details
    if (
      totalPrizePoolValue <= SCRUTINEER_PRIZE_THRESHOLD &&
      (drawData.totalPrizePoolValue || 0) > SCRUTINEER_PRIZE_THRESHOLD
    ) {
      updates.scrutineerSettings = {
        scrutineerRequired: undefined,
        scrutineerName: undefined,
        scrutineerEmail: undefined,
        scrutineerConfirmation: undefined,
      };
    }

    updateDrawData(updates);
  };

  const totalWinners = drawData.prizes.reduce(
    (sum, prize) => sum + (prize.winners || 0),
    0
  );
  const totalReserves = drawData.prizes.reduce(
    (sum, prize) => sum + (prize.reserves || 0),
    0
  );

  const getMaxEntries = (): number => {
    if (drawData.entryMethod === "simple") {
      return drawData.entryCount || 20;
    } else if (
      drawData.entryMethod === "standard" ||
      drawData.entryMethod === "advanced"
    ) {
      return drawData.entryCount || 0;
    }
    return 20;
  };

  const maxEntries = getMaxEntries();

  const entryMethodAllowsMultipleEntries =
    drawData.entryMethod === "advanced" ||
    drawData.csvConfirmationSettings?.fileStructure === "count-per-entrant";
  const showSaPrizeLimitNote =
    drawData.maxPrizesPerEntrant === "one" && entryMethodAllowsMultipleEntries;

  /**
   * NEW VALIDATION LOGIC - Centralized for easy rollback
   * Returns: { isValid: boolean, error?: string, warning?: string }
   * https://plexlogic.atlassian.net/browse/RAND-272
   * To rollback: replace this function with the old validation logic and remove PlxsAlert usage
   */
  const validateEntrantsWinnersReserves = (): {
    isValid: boolean;
    error?: React.ReactNode;
    warning?: React.ReactNode;
  } => {
    const totalEntries = maxEntries;
    const totalWinnersCount = totalWinners;
    const totalReservesCount = totalReserves;
    const totalWinnersAndReserves = totalWinnersCount + totalReservesCount;
    const isOnePrizePerWinner = drawData.maxPrizesPerEntrant === "one";

    // Check if draw uses ticket count column (count-per-entrant file structure)
    const hasTicketCountColumn = 
      drawData.csvConfirmationSettings?.fileStructure === 'count-per-entrant' &&
      !!drawData.csvConfirmationSettings?.ticketCountColumn;

    // For one prize per winner draws
    if (isOnePrizePerWinner) {
      // Only block if there are MORE winners than entries (strict inequality)
      // Equal is OK - we can fill all winner slots
      if (totalWinnersCount > totalEntries) {
        return {
          isValid: false,
          error: hasTicketCountColumn ? (
            <>
              Total number of winners ({totalWinnersCount.toLocaleString()}) exceeds total number of entrants ({totalEntries.toLocaleString()}). Please reduce the number of winners or increase entries.
              <br /><br />
              If this is intentional, and should be drawn, please email{" "}
              <PlxsTextLink href="mailto:support@plexus.co">support@plexus.co</PlxsTextLink>
              {" "}and we will process this draw on your behalf.
            </>
          ) : `Total number of winners (${totalWinnersCount.toLocaleString()}) exceeds total number of entrants (${totalEntries.toLocaleString()}). Please reduce the number of winners or increase entries.`,
        };
      }
      
      // Warn if we can't fill all reserves (winners + reserves > entries)
      // But allow the draw to proceed - it will fill as many reserves as possible
      if (totalWinnersAndReserves > totalEntries) {
        return {
          isValid: true,
          warning: `Total number of entrants (${totalEntries.toLocaleString()}) is less than the combined total of winners and reserves (${totalWinnersAndReserves.toLocaleString()}). The draw will proceed, but not all reserve slots will be filled.`,
        };
      }
      
      // All good - enough entries for winners and reserves
      return { isValid: true };
    }
    
    // For multiple prizes per winner draws
    if (drawData.maxPrizesPerEntrant === "multiple") {
      // Only block if there are MORE winners than entries (strict inequality)
      // Equal is OK - we can fill all winner slots (entries can win multiple times)
      if (totalWinnersCount > totalEntries) {
        return {
          isValid: false,
          error: hasTicketCountColumn ? (
            <>
              Total number of winners ({totalWinnersCount.toLocaleString()}) exceeds total number of entries ({totalEntries.toLocaleString()}). Please reduce the number of winners or increase entries.
              <br /><br />
              If this is intentional, and should be drawn, please email{" "}
              <PlxsTextLink href="mailto:support@plexus.co">support@plexus.co</PlxsTextLink>
              {" "}and we will process this draw on your behalf.
            </>
          ) : `Total number of winners (${totalWinnersCount.toLocaleString()}) exceeds total number of entries (${totalEntries.toLocaleString()}). Please reduce the number of winners or increase entries.`,
        };
      }
      
      // Warn if we can't fill all reserves (winners + reserves > practical limit)
      // But allow the draw to proceed - it will fill as many reserves as possible
      if (totalWinnersAndReserves > totalEntries) {
        return {
          isValid: true,
          warning: `Total number of entries (${totalEntries.toLocaleString()}) is less than the combined total of winners and reserves (${totalWinnersAndReserves.toLocaleString()}). The draw will proceed, but not all reserve slots may be filled.`,
        };
      }
      
      // All good - enough entries for winners and reserves
      return { isValid: true };
    }

    return { isValid: true };
  };

  const validationResult = validateEntrantsWinnersReserves();

  const isFormValid = () => {
    const prizesValid = drawData.prizes.every(
      (prize) => prize.winners > 0 && 
      prize.reserves >= 0 && 
      // Prize Value Check
      getDrawValueError(prize) === undefined
    );
    // Use new validation logic
    const entrantsWinnersValid = validationResult.isValid;

    const scrutineerValid =
      (drawData.totalPrizePoolValue || 0) <= SCRUTINEER_PRIZE_THRESHOLD ||
      drawData.scrutineerSettings?.scrutineerRequired === true ||
      drawData.scrutineerSettings?.scrutineerRequired === false;

    const prizesPerEntrantValid = drawData.maxPrizesPerEntrant !== undefined;

    return (
      prizesValid && entrantsWinnersValid && scrutineerValid && prizesPerEntrantValid
    );
  };

  const handleNext = () => {
    if (!isFormValid()) {
      setShowErrors(true);

      // Build the field list in top-to-bottom DOM order and scroll to the
      // first one in error.
      const errorFields: ErrorField[] = [
        ...drawData.prizes.flatMap((prize, index) => [
          {
            hasError: getDrawValueError(prize) !== undefined,
            testId: `prize-estimated-value-${index}`,
          },
          {
            hasError: getWinnersError(prize) !== undefined,
            testId: `prize-winners-${index}`,
          },
          {
            hasError: getReservesError(prize) !== undefined,
            testId: `prize-reserves-${index}`,
          },
        ]),
        {
          hasError: !validationResult.isValid,
          testId: "entrants-validation-error",
        },
        {
          hasError:
            (drawData.totalPrizePoolValue || 0) > SCRUTINEER_PRIZE_THRESHOLD &&
            drawData.scrutineerSettings?.scrutineerRequired === undefined,
          testId: "scrutineer-required-radio-group",
        },
        {
          hasError: !drawData.maxPrizesPerEntrant,
          testId: "max-prizes-radio-group",
        },
      ];

      scrollToFirstError(errorFields);
      return;
    }

    setShowErrors(false);
    onNext();
  };

  const PRIZE_SETUP_DESCRIPTION = `Add all prizes. Winners are drawn in the order listed.`;

  return (
    <div
      style={{
        width: "100%",
        maxWidth: "100%",
        padding: "0",
        boxSizing: "border-box",
      }}
    >
      <WizardStepHeader
        title="What can people win?"
        description={PRIZE_SETUP_DESCRIPTION}
      />

      {drawData.prizes.map((prize, index) => (
        <div
          key={index}
          style={{
            marginBottom: "var(--plexus-space-lg)",
            padding: "var(--plexus-space-lg)",
            backgroundColor: "var(--plexus-color-surface-50)",
            borderRadius: "var(--plexus-space-xs)",
            border: `var(--plexus-border-width-thin) solid var(--plexus-color-neutral-600)`,
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            minWidth: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "var(--plexus-space-lg)",
              paddingBottom: "var(--plexus-space-sm)",
              flexWrap: "wrap",
              gap: "var(--plexus-space-sm)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--plexus-space-sm)",
                fontSize: "1.125rem",
                fontWeight: 600,
                flex: 1,
                color: "var(--plexus-color-neutral-100)",
                minWidth: "fit-content",
              }}
            >
              <PlxsIcon name="gift" />
              <span>Prize {index + 1}</span>
            </div>
            {drawData.prizes.length > 1 && (
              <PlxsButton
                dataTestId={`remove-prize-${index}`}
                functionalIcon="bin"
                isIconOnly={true}
                tone="urgent"
                variant="secondary"
                onClick={() => removePrize(index)}
                aria-label={`Remove prize ${index + 1}`}
                label={""}
                isDisabled={isLocked}
              />
            )}
          </div>

          <div
            style={{
              marginRight: "2rem",
              display: "flex",
              flexDirection: "column",
              gap: "var(--plexus-space-xs)",
              maxWidth: "80vh",
            }}
          >
            <PlxsField label="Description">
              <PlxsTextField
                dataTestId={`prize-description-${index}`}
                value={prize.description}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updatePrize(index, "description", e.target.value)
                }
                placeholder="e.g. Gift voucher"
                styleOverrides={{ hidePlaceholderEllipsis: true }}
                isDisabled={isLocked}
              />
            </PlxsField>

            <PlxsField label="Estimated value">
              <PlxsTextField
                dataTestId={`prize-estimated-value-${index}`}
                value={prize.estimatedValue?.toString() || ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updatePrize(
                    index,
                    "estimatedValue",
                    e.target.value === "" ? 0 : parseFloat(e.target.value) || 0
                  )
                }
                placeholder="e.g., 1000"
                styleOverrides={{ hidePlaceholderEllipsis: true }}
                isDisabled={isLocked}
                hasError={
                  ((prize.estimatedValue ?? 0) > LARGEST_PRIZE_VALUE ||
                    showErrors) &&
                  getDrawValueError(prize) !== undefined
                }
                errorMessage={
                  ((prize.estimatedValue ?? 0) > LARGEST_PRIZE_VALUE ||
                    showErrors) &&
                  getDrawValueError(prize)
                    ? getDrawValueError(prize)
                    : undefined
                }
              />
            </PlxsField>

            <PlxsField label="Number of winners">
              <PlxsTextField
                dataTestId={`prize-winners-${index}`}
                value={prize.winners?.toString() || ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updatePrize(
                    index,
                    "winners",
                    e.target.value === "" ? 0 : parseInt(e.target.value) || 0
                  )
                }
                placeholder="Enter the number of winners"
                styleOverrides={{ hidePlaceholderEllipsis: true }}
                isDisabled={isLocked}
                hasError={showErrors && getWinnersError(prize) !== undefined}
                errorMessage={
                  showErrors && getWinnersError(prize)
                    ? getWinnersError(prize)
                    : undefined
                }
              />
            </PlxsField>

            <PlxsField label="Number of reserve winners">
              <PlxsTextField
                dataTestId={`prize-reserves-${index}`}
                value={prize.reserves?.toString() || ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updatePrize(
                    index,
                    "reserves",
                    e.target.value === "" ? 0 : parseInt(e.target.value) || 0
                  )
                }
                placeholder="Enter the number of reserve winners"
                styleOverrides={{ hidePlaceholderEllipsis: true }}
                instructionText="We suggest 2–3, in case a winner can't collect."
                isDisabled={isLocked}
                hasError={showErrors && getReservesError(prize) !== undefined}
                errorMessage={
                  showErrors && getReservesError(prize)
                    ? getReservesError(prize)
                    : undefined
                }
              />
            </PlxsField>
          </div>
        </div>
      ))}

      <div
        style={{
          display: "flex",
          justifyContent: "start",
          marginBottom: "var(--plexus-space-lg)",
        }}
      >
        <PlxsButton
          dataTestId="draw-wizard-add-prize-button"
          size="lg"
          label="Add another prize"
          variant="secondary"
          tone="brand"
          onClick={addPrize}
          isDisabled={isLocked}
        />
      </div>

      {(drawData.totalPrizePoolValue || 0) > SCRUTINEER_PRIZE_THRESHOLD && (
        <>
          <WizardStepQuickCheck
            message="Does this draw need a scrutineer?"
            dataTestId="scrutineer-requirements-callout"
          >
            <ScrutineerRequirementText />
          </WizardStepQuickCheck>

          <div
            style={{
              marginBottom: "var(--plexus-space-lg)",
              backgroundColor: "var(--plexus-color-surface-50)",
              padding: "var(--plexus-space-lg)",
              borderRadius: "var(--plexus-space-xs)",
              border: `var(--plexus-border-width-thin) solid var(--plexus-color-neutral-600)`,
            }}
          >
            <PlxsField
              hasError={
                showErrors &&
                drawData.scrutineerSettings?.scrutineerRequired === undefined
              }
              errorMessage={
                showErrors &&
                drawData.scrutineerSettings?.scrutineerRequired === undefined
                  ? "Please select one of the options above"
                  : undefined
              }
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--plexus-space-md)",
                }}
              >
                <PlxsText>I understand, and:</PlxsText>
                <WizardRadioButtonGroup
                  dataTestId="scrutineer-required-radio-group"
                  name="scrutineer-required"
                  isDisabled={isLocked}
                  options={[
                    {
                      value: "required",
                      label: "A scrutineer will witness the draw",
                    },
                    {
                      value: "not-required",
                      label: "No scrutineer required",
                    },
                  ]}
                  value={
                    drawData.scrutineerSettings?.scrutineerRequired === true
                      ? "required"
                      : drawData.scrutineerSettings?.scrutineerRequired === false
                      ? "not-required"
                      : ""
                  }
                  onChange={(value) => {
                    if (value === "required") {
                      updateDrawData({
                        scrutineerSettings: {
                          ...drawData.scrutineerSettings,
                          scrutineerRequired: true,
                        },
                      });
                    } else {
                      updateDrawData({
                        scrutineerSettings: {
                          scrutineerRequired: false,
                          scrutineerName: undefined,
                          scrutineerEmail: undefined,
                          scrutineerConfirmation: undefined,
                        },
                      });
                    }
                  }}
                />
              </div>
            </PlxsField>
          </div>
        </>
      )}

      <div
        style={{
          marginBottom: "var(--plexus-space-lg)",
          backgroundColor: "var(--plexus-color-surface-50)",
          padding: "var(--plexus-space-lg)",
          borderRadius: "var(--plexus-space-xs)",
          border: `var(--plexus-border-width-thin) solid var(--plexus-color-neutral-600)`,
        }}
      >
        <h3
          style={{
            fontSize: "1.125rem",
            fontWeight: 600,
            margin: "0 0 var(--plexus-space-sm) 0",
            color: "var(--plexus-color-neutral-100)",
          }}
        >
          Prize limits
        </h3>
        <div style={{ marginBottom: "var(--plexus-space-md)" }}>
          <PlxsText size="sm">
            Entrants who enter more than once can win:
          </PlxsText>
        </div>
        <PlxsField
          hasError={showErrors && !drawData.maxPrizesPerEntrant}
          errorMessage={
            showErrors && !drawData.maxPrizesPerEntrant
              ? "Please select one of the options above"
              : undefined
          }
        >
          <WizardRadioButtonGroup
            dataTestId="max-prizes-radio-group"
            name="max-prizes-per-entrant"
            isDisabled={isLocked}
            options={[
              { value: "one", label: "One prize each" },
              { value: "multiple", label: "Multiple prizes" },
            ]}
            value={drawData.maxPrizesPerEntrant || "one"}
            onChange={(value) => {
              updateDrawData({
                maxPrizesPerEntrant: value as "one" | "multiple",
              });
            }}
          />
        </PlxsField>
      </div>

      {showSaPrizeLimitNote && (
        <WizardStepQuickCheck
          message="Heads up:"
          dataTestId="prizes-per-entrant-callout"
        >
          <PlxsText>
            {getTextSnippet("saPrizeLimit").prefix}{" "}
            <PlxsTextLink
              href={getHelpUrl("saPrizeLimitExclusion")}
              dataTestId="sa-prize-limit-help-link"
              iconName="externalLink"
              target="_blank"
            >
              {getTextSnippet("saPrizeLimit").linkText}
            </PlxsTextLink>
            .
          </PlxsText>
        </WizardStepQuickCheck>
      )}

      <div
        style={{
          marginBottom: "var(--plexus-space-lg)",
          backgroundColor: "var(--plexus-color-surface-50)",
          padding: "var(--plexus-space-lg)",
          borderRadius: "var(--plexus-space-xs)",
          border: `var(--plexus-border-width-thin) solid var(--plexus-color-neutral-600)`,
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
          maxWidth: "100%",
          overflow: "hidden",
        }}
      >
        <PlxsText weight="bold">
          You're set: {totalWinners} winner{totalWinners === 1 ? "" : "s"},{" "}
          {totalReserves} reserve{totalReserves === 1 ? "" : "s"}, $
          {(drawData.totalPrizePoolValue || 0).toLocaleString()} pool.
        </PlxsText>
      </div>

      {/* Validation Alert - Show validation error or warning */}
      {showErrors && validationResult.error && (
        <div style={{ marginBottom: "var(--plexus-space-lg)" }}>
          <PlxsAlert variant="error" dataTestId="entrants-validation-error">
            {validationResult.error}
          </PlxsAlert>
        </div>
      )}
      
      {validationResult.warning && !validationResult.error && (
        <div style={{ marginBottom: "var(--plexus-space-lg)" }}>
          <PlxsAlert variant="warning" dataTestId="entrants-validation-warning">
            {validationResult.warning}
          </PlxsAlert>
        </div>
      )}

      <WizardFooterNav
        backButton={{
          dataTestId: "back-prize-config",
          label: "Back",
          variant: "secondary",
          tone: "neutral",
          onClick: onBack,
        }}
        // With no neutral state for a primary button, we switch the variant and tone based on form validity.
        continueButton={{
          isActive: !isFormValid(),
          dataTestId: "next-prize-config",
          label: "Next",
          variant: isFormValid() ? "primary" : "secondary",
          tone: isFormValid() ? "positive" : "neutral",
          onClick: handleNext
        }}
      />
    </div>
  );
};

export default PrizeConfigurationStep;
export { getDrawValueError, getWinnersError, getReservesError };